import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Search, Sun, Moon, Calendar, Clock, 
  Compass, ArrowUp, ChevronDown, ChevronUp,
  Target, Filter
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const MOON_PHASE_KEYS = {
  'New Moon': 'moon.newMoon',
  'Waxing Crescent': 'moon.waxingCrescent',
  'First Quarter': 'moon.firstQuarter',
  'Waxing Gibbous': 'moon.waxingGibbous',
  'Full Moon': 'moon.fullMoon',
  'Waning Gibbous': 'moon.waningGibbous',
  'Last Quarter': 'moon.lastQuarter',
  'Waning Crescent': 'moon.waningCrescent',
};

const LIGHT_CONDITION_KEYS = {
  'Day': 'findsunmoon.day',
  'Night': 'timeline.night',
  'Astro Twilight': 'findsunmoon.astroTwilight',
  'Nautical Twilight': 'findsunmoon.nauticalTwilight',
  'Blue Hour': 'sun.blueHour',
  'Golden Hour': 'sun.goldenHour',
};

const SORT_KEYS = {
  date: 'findsunmoon.sortDate',
  elevation: 'findsunmoon.sortElevation',
  phase: 'findsunmoon.sortPhase',
};

const FindSunMoon = ({ latitude, longitude, onDateSelect }) => {
  const { t } = useI18n();
  const [searchType, setSearchType] = useState('sun'); // 'sun' or 'moon'
  const [targetAzimuth, setTargetAzimuth] = useState(270); // West
  const [targetElevation, setTargetElevation] = useState(5); // Near horizon
  const [azimuthTolerance, setAzimuthTolerance] = useState(3);
  const [elevationTolerance, setElevationTolerance] = useState(2);
  const [dateRange, setDateRange] = useState(365); // Days to search
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date'); // 'date', 'elevation', 'phase'

  // Calculate sun position for a given date and time
  const calculateSunPosition = useCallback((date) => {
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
    const hour = date.getHours() + date.getMinutes() / 60;
    
    // Solar declination
    const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
    
    // Hour angle
    const solarNoon = 12.0;
    const hourAngle = 15 * (hour - solarNoon);
    
    // Convert to radians
    const latRad = latitude * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    const haRad = hourAngle * Math.PI / 180;
    
    // Calculate altitude
    const sinAlt = Math.sin(latRad) * Math.sin(decRad) + 
                   Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
    
    // Calculate azimuth
    const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitude * Math.PI / 180)) / 
                  (Math.cos(latRad) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (hourAngle > 0) azimuth = 360 - azimuth;
    
    return { azimuth, altitude };
  }, [latitude]);

  // Calculate moon position (simplified)
  const calculateMoonPosition = useCallback((date) => {
    // Simplified moon position calculation
    const daysSinceJ2000 = (date - new Date(2000, 0, 1, 12, 0, 0)) / 86400000;
    
    // Mean longitude
    const L = (218.316 + 13.176396 * daysSinceJ2000) % 360;
    // Mean anomaly
    const M = (134.963 + 13.064993 * daysSinceJ2000) % 360;
    // Mean distance
    const F = (93.272 + 13.229350 * daysSinceJ2000) % 360;
    
    // Ecliptic longitude
    const lon = L + 6.289 * Math.sin(M * Math.PI / 180);
    // Ecliptic latitude
    const lat = 5.128 * Math.sin(F * Math.PI / 180);
    
    // Convert to equatorial (simplified)
    const obliquity = 23.439;
    const lonRad = lon * Math.PI / 180;
    const latRad = lat * Math.PI / 180;
    const oblRad = obliquity * Math.PI / 180;
    
    const ra = Math.atan2(
      Math.sin(lonRad) * Math.cos(oblRad) - Math.tan(latRad) * Math.sin(oblRad),
      Math.cos(lonRad)
    ) * 180 / Math.PI;
    
    const dec = Math.asin(
      Math.sin(latRad) * Math.cos(oblRad) + 
      Math.cos(latRad) * Math.sin(oblRad) * Math.sin(lonRad)
    ) * 180 / Math.PI;
    
    // Calculate hour angle
    const hour = date.getHours() + date.getMinutes() / 60;
    const lst = (100.46 + 0.985647 * daysSinceJ2000 + longitude + hour * 15) % 360;
    const ha = lst - ra;
    
    // Convert to altitude/azimuth
    const latRadObs = latitude * Math.PI / 180;
    const decRad = dec * Math.PI / 180;
    const haRad = ha * Math.PI / 180;
    
    const sinAlt = Math.sin(latRadObs) * Math.sin(decRad) + 
                   Math.cos(latRadObs) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
    
    const cosAz = (Math.sin(decRad) - Math.sin(latRadObs) * Math.sin(altitude * Math.PI / 180)) / 
                  (Math.cos(latRadObs) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (Math.sin(haRad) > 0) azimuth = 360 - azimuth;
    
    return { azimuth, altitude };
  }, [latitude, longitude]);

  // Get moon phase
  const getMoonPhase = (date) => {
    const lp = 2551443;
    const newMoon = new Date(1970, 0, 7, 20, 35, 0).getTime() / 1000;
    const phase = ((date.getTime() / 1000 - newMoon) % lp) / lp;
    return phase;
  };

  const getMoonPhaseName = (phase) => {
    if (phase < 0.03 || phase > 0.97) return 'New Moon';
    if (phase < 0.22) return 'Waxing Crescent';
    if (phase < 0.28) return 'First Quarter';
    if (phase < 0.47) return 'Waxing Gibbous';
    if (phase < 0.53) return 'Full Moon';
    if (phase < 0.72) return 'Waning Gibbous';
    if (phase < 0.78) return 'Last Quarter';
    return 'Waning Crescent';
  };

  // Search for matching dates
  const performSearch = useCallback(() => {
    setIsSearching(true);
    const foundResults = [];
    
    const startDate = new Date();
    const endDate = new Date(startDate.getTime() + dateRange * 24 * 60 * 60 * 1000);
    
    const calculatePosition = searchType === 'sun' ? calculateSunPosition : calculateMoonPosition;
    
    // Search each day
    let currentDate = new Date(startDate);
    while (currentDate < endDate) {
      // Check every 15 minutes during the day
      for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
        const checkDate = new Date(currentDate);
        checkDate.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
        
        const pos = calculatePosition(checkDate);
        
        // Check if position matches target
        const azDiff = Math.abs(pos.azimuth - targetAzimuth);
        const azMatch = azDiff <= azimuthTolerance || (360 - azDiff) <= azimuthTolerance;
        const elMatch = Math.abs(pos.altitude - targetElevation) <= elevationTolerance;
        
        if (azMatch && elMatch && pos.altitude > -5) {
          // Refine the time
          let bestTime = checkDate;
          let bestDiff = Math.abs(pos.azimuth - targetAzimuth) + Math.abs(pos.altitude - targetElevation);
          
          for (let fine = -15; fine <= 15; fine += 1) {
            const fineDate = new Date(checkDate.getTime() + fine * 60 * 1000);
            const finePos = calculatePosition(fineDate);
            const fineDiff = Math.abs(finePos.azimuth - targetAzimuth) + Math.abs(finePos.altitude - targetElevation);
            if (fineDiff < bestDiff) {
              bestDiff = fineDiff;
              bestTime = fineDate;
            }
          }
          
          const finalPos = calculatePosition(bestTime);
          const moonPhase = getMoonPhase(bestTime);
          
          // Determine light condition
          let lightCondition = 'Day';
          const sunPos = calculateSunPosition(bestTime);
          if (sunPos.altitude < -18) lightCondition = 'Night';
          else if (sunPos.altitude < -12) lightCondition = 'Astro Twilight';
          else if (sunPos.altitude < -6) lightCondition = 'Nautical Twilight';
          else if (sunPos.altitude < 0) lightCondition = 'Blue Hour';
          else if (sunPos.altitude < 6) lightCondition = 'Golden Hour';
          
          foundResults.push({
            date: bestTime,
            azimuth: finalPos.azimuth,
            elevation: finalPos.altitude,
            moonPhase: moonPhase,
            moonPhaseName: getMoonPhaseName(moonPhase),
            lightCondition: lightCondition
          });
          
          // Skip ahead to avoid duplicates
          minutes += 60;
        }
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Remove duplicates (same day, similar time)
    const uniqueResults = foundResults.filter((result, index, arr) => {
      return !arr.slice(0, index).some(r => 
        r.date.toDateString() === result.date.toDateString() &&
        Math.abs(r.date.getHours() - result.date.getHours()) < 2
      );
    });
    
    setResults(uniqueResults.slice(0, 50)); // Limit to 50 results
    setIsSearching(false);
  }, [searchType, targetAzimuth, targetElevation, azimuthTolerance, elevationTolerance, dateRange, calculateSunPosition, calculateMoonPosition]);

  // Sort results
  const sortedResults = [...results].sort((a, b) => {
    if (sortBy === 'date') return a.date - b.date;
    if (sortBy === 'elevation') return b.elevation - a.elevation;
    if (sortBy === 'phase') return a.moonPhase - b.moonPhase;
    return 0;
  });

  const getDirectionName = (azimuth) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(azimuth / 45) % 8;
    return directions[index];
  };

  const getLightColor = (condition) => {
    switch (condition) {
      case 'Golden Hour': return 'text-amber-500 bg-amber-500/20';
      case 'Blue Hour': return 'text-blue-500 bg-blue-500/20';
      case 'Night': return 'text-slate-400 bg-slate-500/20';
      case 'Astro Twilight': return 'text-indigo-400 bg-indigo-500/20';
      case 'Nautical Twilight': return 'text-purple-400 bg-purple-500/20';
      default: return 'text-yellow-500 bg-yellow-500/20';
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Configuration */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Search className="w-4 h-4 text-cyan-400" />
            {t('findsunmoon.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Type selector */}
          <div className="flex gap-2">
            <Button
              variant={searchType === 'sun' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSearchType('sun')}
              className={searchType === 'sun' ? 'bg-amber-500 hover:bg-amber-600' : 'border-white/10'}
            >
              <Sun className="w-4 h-4 mr-1" />
              {t('findsunmoon.sun')}
            </Button>
            <Button
              variant={searchType === 'moon' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSearchType('moon')}
              className={searchType === 'moon' ? 'bg-blue-500 hover:bg-blue-600' : 'border-white/10'}
            >
              <Moon className="w-4 h-4 mr-1" />
              {t('findsunmoon.moon')}
            </Button>
          </div>

          {/* Target Position */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Compass className="w-3 h-3" />
                {t('findsunmoon.targetAzimuth')}: {targetAzimuth}° ({getDirectionName(targetAzimuth)})
              </Label>
              <Slider
                value={[targetAzimuth]}
                onValueChange={([v]) => setTargetAzimuth(v)}
                min={0}
                max={360}
                step={1}
              />
              <div className="flex gap-1 flex-wrap">
                {[
                  { label: 'N', az: 0 },
                  { label: 'E', az: 90 },
                  { label: 'S', az: 180 },
                  { label: 'W', az: 270 },
                ].map(d => (
                  <Button
                    key={d.label}
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setTargetAzimuth(d.az)}
                  >
                    {d.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <ArrowUp className="w-3 h-3" />
                {t('findsunmoon.targetElevation')}: {targetElevation}°
              </Label>
              <Slider
                value={[targetElevation]}
                onValueChange={([v]) => setTargetElevation(v)}
                min={-5}
                max={90}
                step={1}
              />
              <div className="flex gap-1 flex-wrap">
                {[
                  { labelKey: 'findsunmoon.horizon', el: 0 },
                  { labelKey: 'findsunmoon.low', el: 15 },
                  { labelKey: 'findsunmoon.mid', el: 45 },
                  { labelKey: 'findsunmoon.high', el: 70 },
                ].map(d => (
                  <Button
                    key={d.labelKey}
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setTargetElevation(d.el)}
                  >
                    {t(d.labelKey)}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-3 h-3 mr-1" />
            {t('findsunmoon.advancedFilters')}
            {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>

          {showFilters && (
            <div className="grid grid-cols-3 gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div>
                <Label className="text-xs text-slate-500">{t('findsunmoon.azTolerance')}</Label>
                <Input
                  type="number"
                  value={azimuthTolerance}
                  onChange={(e) => setAzimuthTolerance(Number(e.target.value))}
                  className="h-8 text-xs bg-slate-900"
                  min={1}
                  max={30}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t('findsunmoon.elTolerance')}</Label>
                <Input
                  type="number"
                  value={elevationTolerance}
                  onChange={(e) => setElevationTolerance(Number(e.target.value))}
                  className="h-8 text-xs bg-slate-900"
                  min={1}
                  max={20}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t('findsunmoon.daysToSearch')}</Label>
                <Input
                  type="number"
                  value={dateRange}
                  onChange={(e) => setDateRange(Number(e.target.value))}
                  className="h-8 text-xs bg-slate-900"
                  min={30}
                  max={730}
                />
              </div>
            </div>
          )}

          {/* Search Button */}
          <Button
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
            onClick={performSearch}
            disabled={isSearching}
          >
            {isSearching ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t('findsunmoon.searching')}
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                {t('findsunmoon.findDates')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Card className="bg-slate-900/50 border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                {t('findsunmoon.found')} {results.length} {t('findsunmoon.results')}
              </CardTitle>
              <div className="flex gap-1">
                {['date', 'elevation', 'phase'].map(s => (
                  <Button
                    key={s}
                    variant={sortBy === s ? 'default' : 'ghost'}
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setSortBy(s)}
                  >
                    {t(SORT_KEYS[s])}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {sortedResults.map((result, index) => (
              <div
                key={index}
                className="p-3 bg-slate-800/50 rounded-lg border border-white/5 hover:border-cyan-500/30 cursor-pointer transition-all"
                onClick={() => onDateSelect && onDateSelect(result.date)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">
                        {result.date.getDate()}
                      </div>
                      <div className="text-xs text-slate-500">
                        {result.date.toLocaleString('default', { month: 'short' })}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-sm text-white">
                          {result.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <Badge className={`text-xs ${getLightColor(result.lightCondition)}`}>
                          {t(LIGHT_CONDITION_KEYS[result.lightCondition] || result.lightCondition)}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {t('findsunmoon.az')}: {result.azimuth.toFixed(1)}° • {t('findsunmoon.el')}: {result.elevation.toFixed(1)}°
                      </div>
                    </div>
                  </div>
                  {searchType === 'moon' && (
                    <div className="text-right">
                      <div className="text-xs text-slate-500">{t(MOON_PHASE_KEYS[result.moonPhaseName] || result.moonPhaseName)}</div>
                      <div className="text-xs text-blue-400">
                        {Math.round(result.moonPhase < 0.5 ? result.moonPhase * 200 : (1 - result.moonPhase) * 200)}% {t('moon.illuminated')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3 text-xs text-slate-400 space-y-1">
          <p>🎯 <strong>{t('findsunmoon.useCaseLabel')}</strong> {t('findsunmoon.useCaseText')}</p>
          <p>📐 <strong>{t('findsunmoon.azimuthLabel')}</strong> {t('findsunmoon.azimuthText')}</p>
          <p>📅 <strong>{t('findsunmoon.clickResultLabel')}</strong> {t('findsunmoon.clickResultText')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FindSunMoon;
